// src/components/timetable/modern/ModernTimetable.tsx
//
// Ported from the mobile app's Timetable.tsx. RN's GlobalHeader/SafeAreaView/
// CircularLoader/RefreshControl become a plain page wrapper + GlobalHeaderTitle
// (same header component Timetable.tsx uses), with refresh as one of its buttons.

import React from 'react';
import ModernTimetableHeader from './ModernTimetableHeader';
import ModernTimetableGrid from './ModernTimetableGrid';
import ModernTeacherPickerModal from './ModernTeacherPickerModal';
import { C } from './modernTimetableTypes';
import { useModernTimetable } from './useModernTimetable';
import GlobalHeaderTitle from '../../../components/GlobalHeaderTitle';

const ModernTimetable: React.FC = () => {
  const tt = useModernTimetable();

  return (
    <>
      <style>{`
        .modern-timetable-page { min-height: 100%; display: flex; flex-direction: column; gap: 14px; }
        .modern-timetable-loading {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 12px; padding: 80px 20px; color: ${C.textSecondary}; font-size: 13.5px; font-weight: 600;
        }
      `}</style>

      <div className="modern-timetable-page">
        <GlobalHeaderTitle
          title="Timetable"
          subtitle={`${tt.timetableTypeName} • ${tt.selectedDay}`}
          icon="pi pi-clock"
          buttons={[
            {
              icon: tt.refreshing ? 'pi pi-spin pi-spinner' : 'pi pi-refresh',
              label: 'Refresh',
              onClick: tt.onRefresh,
              outlined: true,
              disabled: tt.refreshing || tt.initialLoading,
            },
          ]}
        />

        {tt.initialLoading ? (
          <div className="modern-timetable-loading">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: 26, color: C.primary }} />
            Loading timetable...
          </div>
        ) : (
          <>
            <ModernTimetableHeader
              timetableTypeName={tt.timetableTypeName}
              sectionCount={tt.classSectionCols.length}
              teacherCount={tt.allTeachers.length}
              periodCount={tt.uniquePeriods.length}
              presentCount={tt.presentCount}
              typeOptions={tt.typeOptions}
              typeOptionsLoading={tt.typeOptionsLoading}
              selectedTypeValue={tt.selectedTypeValue}
              onTypeChange={tt.setSelectedTypeValue}
              viewMode={tt.viewMode}
              onViewModeChange={tt.setViewMode}
              selectedDay={tt.selectedDay}
              onDayChange={tt.setSelectedDay}
              selectedTeacher={tt.selectedTeacher}
              busyCount={tt.busyCount}
              vacantCount={tt.vacantCount}
              isSelectedPresent={!!(tt.selectedTeacher && tt.presentTeachers.has(tt.selectedTeacher))}
              onOpenPicker={() => tt.setPickerVisible(true)}
              fadeIn={tt.fadeIn}
            />

            <ModernTimetableGrid
              timetableData={tt.timetableData}
              allItems={tt.allItems}
              periodMap={tt.periodMap}
              uniquePeriods={tt.uniquePeriods}
              classSectionCols={tt.classSectionCols}
              allTeachers={tt.allTeachers}
              presentTeachers={tt.presentTeachers}
              selectedDay={tt.selectedDay}
              selectedTeacher={tt.selectedTeacher}
              viewMode={tt.viewMode}
              gridLoading={tt.gridLoading}
              error={tt.error}
              fadeIn={tt.fadeIn}
              arrangementPeriods={tt.arrangementPeriods}
              arrangements={tt.arrangements}
              arrangementsLoading={tt.arrangementsLoading}
              arrangementsError={tt.arrangementsError}
              addArrangement={tt.addArrangement}
              deleteArrangement={tt.deleteArrangement}
              findClassItem={tt.findClassItem}
              findTeacherItem={tt.findTeacherItem}
              findTeacherPeriodItem={tt.findTeacherPeriodItem}
              isBreakPeriod={tt.isBreakPeriod}
              onRetry={() => tt.fetchTimetable(false)}
            />
          </>
        )}
      </div>

      <ModernTeacherPickerModal
        visible={tt.pickerVisible}
        teachers={tt.allTeachers}
        selected={tt.selectedTeacher}
        onSelect={tt.setSelectedTeacher}
        onClose={() => tt.setPickerVisible(false)}
        presentTeachers={tt.presentTeachers}
      />
    </>
  );
};

export default ModernTimetable;